(ns frontend.updates
  (:require
   [comms.sente :as sente]
   [common.time :as c-time]))

(defonce streams* (atom {}))
(defonce initialized?* (atom false))

(defn- next-stream-version
  [current-version]
  (if (number? current-version)
    (inc current-version)
    (c-time/now-millis)))

(defn- classify-reply
  "A transport failure becomes a sentence here; `:retry?` carries the fact
   that it is worth another attempt, so nothing downstream needs the raw
   Sente keyword to decide that."
  [reply]
  (cond
    (sente/transport-failure? reply) {:ok? false
                                      :retry? true
                                      :error (sente/transport-failure-message reply)}
    (and (map? reply)
         (#{:error :err :failed} (:status reply))) {:ok? false
                                                    :retry? false
                                                    :error reply}
    ;; A registered endpoint reports a failure as a map with a keyword
    ;; :error, which backend.transport.core/error-result? also tests. The
    ;; :status branch above covers only the Sente router's own catch.
    (and (map? reply)
         (keyword? (:error reply))) {:ok? false
                                     :retry? false
                                     :error reply}
    :else {:ok? true
           :data reply}))

(defn- send-subscribe!
  [{:keys [stream-id stream-version feed-key request connection on-open on-error]}]
  (letfn [(attempt []
            (let [current (get @streams* stream-id)]
              (when (= stream-version (:stream-version current))
                (if (sente/open?)
                  (sente/rpc!
                   [:updates/open-stream
                    {:stream-id      stream-id
                     :stream-version stream-version
                     :feed-key       feed-key
                     :request        request
                     :connection     connection}]
                   (fn [reply]
                     (let [{:keys [ok? data error retry?]} (classify-reply reply)]
                       (if ok?
                         (do
                           (when-let [initial-message (:initial-message data)]
                             (swap! streams* update stream-id
                                    (fn [current-stream]
                                      (cond-> current-stream
                                        (:basis-t initial-message)
                                        (assoc :last-basis-t (:basis-t initial-message)))))
                             (when-let [on-message (:on-message (get @streams* stream-id))]
                               (on-message initial-message)))
                           (when (fn? on-open)
                             (on-open data)))
                         (do
                           (when (fn? on-error)
                             (on-error error))
                           (when retry?
                             (js/setTimeout attempt 1000)))))))
                  (js/setTimeout attempt 1000)))))]
    (attempt)))

(defn- resubscribe-active-streams!
  "Sends every stream in streams* again on the socket that is open now, so the
   server registers it under the uid of the new handshake.

   This does not check which account opened a stream. A tenant switch today
   needs a logout, and logout reloads the page, so streams* is empty at that
   time. If a tenant switch ever occurs without a reload, close the streams of
   the earlier session before the reconnect. If you do not, they are sent again
   in the new tenant."
  []
  (doseq [[stream-id stream] @streams*]
    (let [stream-version (next-stream-version (:stream-version stream))
          connection (cond-> (merge {:client-time (c-time/now-millis)}
                                    (dissoc (:connection stream) :client-time))
                       (:last-basis-t stream)
                       (assoc :since-basis-t (:last-basis-t stream)))
          updated-stream (assoc stream
                                :stream-version stream-version
                                :connection connection)]
      (swap! streams* assoc stream-id updated-stream)
      (send-subscribe! updated-stream))))

(defn- handle-sente-state-change!
  [{:keys [old-state new-state first-connect?]}]
  (let [was-open? (true? (:open? old-state))
        open-now? (true? (:open? new-state))]
    (when (and open-now? (not was-open?) (not first-connect?))
      (resubscribe-active-streams!))))

(defn- handle-updates-push!
  [_event-id {:keys [stream-id message]}]
  ;; Gate on monotonic basis-t rather than stream-version equality: a push is
  ;; applied iff it is strictly newer than the last message we applied. This
  ;; drops only genuinely-stale pushes (preventing out-of-order/duplicate
  ;; application) and never drops a newer delta just because it carries an
  ;; older generation label after a resubscribe.
  (let [stream (get @streams* stream-id)
        incoming-basis-t (:basis-t message)
        last-basis-t (:last-basis-t stream)
        newer? (or (nil? incoming-basis-t)
                   (nil? last-basis-t)
                   (> incoming-basis-t last-basis-t))]
    (when (and stream newer?)
      (swap! streams* update stream-id
             (fn [current]
               (cond-> current
                 incoming-basis-t
                 (assoc :last-basis-t incoming-basis-t))))
      (when-let [on-message (:on-message stream)]
        (on-message message)))))

(defn init!
  []
  (when (compare-and-set! initialized?* false true)
    (sente/add-state-listener! ::updates handle-sente-state-change!)
    (sente/add-recv-listener! ::updates
                              (fn [event-id data]
                                (if (= event-id :updates/push)
                                  (do
                                    (handle-updates-push! event-id data)
                                    true)
                                  false)))))

(defn open!
  [{:keys [stream-id feed-key request connection on-message on-open on-error]}]
  {:pre [(string? stream-id)
         (keyword? feed-key)
         (map? request)]}
  (let [stream-version (next-stream-version (get-in @streams* [stream-id :stream-version]))
        connection (merge {:client-time (c-time/now-millis)} connection)
        stream {:stream-id      stream-id
                :stream-version stream-version
                :feed-key       feed-key
                :request        request
                :connection     connection
                :on-message     on-message
                :on-open        on-open
                :on-error       on-error}]
    (swap! streams* assoc stream-id stream)
    (send-subscribe! stream)
    stream-id))

(defn close!
  [stream-id]
  (let [stream (get @streams* stream-id)]
    (swap! streams* dissoc stream-id)
    (when stream
      (sente/rpc-when-open!
       [:updates/close-stream
        {:stream-id stream-id}]
       (fn [_]
         :ok)))))
